/* eslint-disable react/jsx-no-useless-fragment */
import { ResidentItem } from '@lmc/ts-common'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { ExtraErrorText, ExtraText, GeneralText } from '../styledComponents'
import { EditTooltip } from './EditTooltip'
import useHomeType from 'hooks/useHomeType'
import { getExtra } from '../utils/utils'
import DownloadForm from './DownloadForm'

type GeneralDNACPRSectionProps = {
  resident: ResidentItem
}

export const GeneralDNACPRSection = ({
  resident,
}: GeneralDNACPRSectionProps) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)

  const { isDoLSOverride } = useHomeType()

  const hasAllergies = resident?.residentAllergies?.length > 0

  const hasNoKnownAllergies = resident.noKnownAllergies === true

  return (
    <CoreSectionWrapper>
      <CoreSection>
        <StyledCoreLabel level={2}>{t(supConstants.DNACPR)}</StyledCoreLabel>
        <GeneralText weight="bold">
          <EditTooltip title={t(supConstants.DNACPR_TOOLTIP)}>
            <>
              {(resident?.dnacpr === null || resident?.dnacpr === undefined) &&
                t(supConstants.NO_INFORMATION)}
              {resident?.dnacpr === true && t(supConstants.DO_NOT_CPR)}
              {resident?.dnacpr === false && t(supConstants.CPR)}
            </>
          </EditTooltip>
        </GeneralText>
        <DnacprContainer>
          {resident?.dnacprDetails && (
            <StyledCoreLabel level={2}>
              {resident?.dnacprDetails}
            </StyledCoreLabel>
          )}
          {resident.dnacprForm && resident.dnacpr && (
            <StyledCoreLabel level={2}>•</StyledCoreLabel>
          )}
          {resident.dnacprForm && (
            <DownloadForm
              fileUrl={resident.dnacprForm}
              fileName={`${resident.name.first} ${resident.name.last}'s DNACPR Form`}
            >
              <Text level={2}>{t(supConstants.VIEW_DNACPR_FORM)}</Text>
            </DownloadForm>
          )}
        </DnacprContainer>
      </CoreSection>
      <CoreSection>
        <StyledCoreLabel level={2}>
          {isDoLSOverride
            ? t(supConstants.DEPRIVATION_LIBERTY)
            : t(supConstants.LIBERTY)}
        </StyledCoreLabel>
        <GeneralText weight="bold">
          <EditTooltip
            title={
              isDoLSOverride
                ? t(supConstants.DOLS_TOOLTIP)
                : t(supConstants.LPS_TOOLTIP)
            }
          >
            {resident?.dols ? t(supConstants.YES) : t(supConstants.NO)}
          </EditTooltip>
        </GeneralText>
        {getExtra('dols', resident) === t(supConstants.REVIEW_DATE_OVERDUE) ? (
          <ExtraErrorText level={2}>
            {t(supConstants.REVIEW_DATE_OVERDUE)}
          </ExtraErrorText>
        ) : (
          <ExtraText level={2}>{getExtra('dols', resident)}</ExtraText>
        )}
      </CoreSection>
      {(hasAllergies || hasNoKnownAllergies) && (
        <CoreSection>
          <StyledCoreLabel level={2}>
            {t(supConstants.ALLERGIES)}
          </StyledCoreLabel>
          <GeneralText weight="bold">
            <EditTooltip title={t(supConstants.ALLERGY_TOOLTIP)}>
              <>
                {hasNoKnownAllergies && t(supConstants.NO_KNOWN_ALLERGIES)}
                {!hasNoKnownAllergies
                  ? hasAllergies
                    ? resident?.residentAllergies.map((allergy) => (
                        <AllergyTitle key={allergy.causativeAgent}>
                          {allergy.causativeAgent}
                        </AllergyTitle>
                      ))
                    : t(supConstants.NO)
                  : null}
              </>
            </EditTooltip>
          </GeneralText>
        </CoreSection>
      )}
    </CoreSectionWrapper>
  )
}

const StyledCoreLabel = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const CoreSectionWrapper = styled.div`
  border-top: 1px solid;
  border-color: ${(props) => props.theme.color.neutral.color7};
  margin-top: 16px;
  padding-top: 16px;
  display: flex;
  justify-content: space-between;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    flex-direction: column;
  }
`

const CoreSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-top: 15px;
  }
`

const DnacprContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const AllergyTitle = styled.div`
  display: flex;
  flex-direction: column;
`
